// Edits slice — sparse per-side manual edits. Spec §4.2, §5.4, §5.5, §6.3.2.

import type { StateCreator } from 'zustand';
import { defaultPose } from '../config/defaultPose';
import { KEYPOINTS } from '../config/keypoints';
import type {
  EditChange,
  EditedKeypoint,
  EditMap,
  HistoryEntry,
  KeypointName,
  OutputJSON,
} from '../types';
import type { AppState } from './index';
import type { Side } from './predictions';

export interface DraftRestore {
  front_left: OutputJSON;
  front_right: OutputJSON;
  left?: OutputJSON;
  right?: OutputJSON;
  audited?: number[];
  elapsedMs?: number;
}

type LegacyDraftEdit = Omit<EditedKeypoint, 'visibility'> & {
  visibility: EditedKeypoint['visibility'] | 'not_in_frame';
};

export interface EditsSlice {
  editsFrontLeft: EditMap;
  editsFrontRight: EditMap;
  editsLeft: EditMap;
  editsRight: EditMap;
  setKeypoint: (side: Side, frame: number, name: KeypointName, edit: EditedKeypoint) => void;
  /** Change a keypoint's visibility (visible/occluded/out_of_frame), preserving
   *  the side's own position. Restoring from out_of_frame re-seeds xy from the
   *  prediction. Changes on `front_left` also propagate the same visibility to
   *  `front_right` (visibility only, always overwriting, bypassing the qualified
   *  read-only guard); both writes share one undo step. Not the reverse. */
  setVisibility: (
    side: Side,
    frame: number,
    name: KeypointName,
    visibility: EditedKeypoint['visibility']
  ) => void;
  /** Toggle a qualified keypoint between locked-default and unlocked-for-edit.
   *  First call: creates an edit from the prediction position (locked=false).
   *  Subsequent calls: toggle the locked flag on the existing edit.
   *  No-op for unqualified keypoints. */
  toggleQualifiedKeypoint: (side: Side, frame: number, name: KeypointName) => void;
  clearKeypoint: (side: Side, frame: number, name: KeypointName) => void;
  setAllLocked: (side: Side, frame: number, locked: boolean) => void;
  resetFrame: (side: Side, frame: number) => void;
  resetAllEdits: () => void;
  restoreFromDraft: (draft: DraftRestore) => void;
  /**
   * Seed edits maps from a fresh LoadedSession. Replaces all sides at once
   * and clears history. Image loader uses this to materialize edits from
   * the input `status` field; video loader passes empty maps.
   */
  setInitialEdits: (initial: {
    front_left: EditMap;
    front_right: EditMap;
    left: EditMap;
    right: EditMap;
  }) => void;
  /**
   * secPose v2 only: write carry-forward seeds for an uncommitted frame.
   * Seeds are display defaults, not user actions, so they do not push history.
   */
  seedFrameEdits: (
    frame: number,
    seeds: Partial<Record<Side, Partial<Record<KeypointName, EditedKeypoint>>>>
  ) => void;
  /**
   * secPose v2 only: promote carry-forward seeds to committed edits when a
   * frame is saved, so the carried pose is exported and frozen.
   */
  commitFrameSeeds: (frame: number) => void;
}

function rebuildEditMap(source: EditMap): EditMap {
  const out: EditMap = {};
  for (const [frameKey, frameEdits] of Object.entries(source)) {
    const normalizedFrame: Partial<Record<KeypointName, EditedKeypoint>> = {};
    for (const [name, edit] of Object.entries(frameEdits ?? {}) as Array<
      [KeypointName, LegacyDraftEdit]
    >) {
      const { visibility, ...rest } = edit;
      normalizedFrame[name] =
        visibility === 'not_in_frame'
          ? { ...rest, visibility: 'out_of_frame', xy: [-1, -1] }
          : { ...rest, visibility };
    }
    out[Number(frameKey)] = normalizedFrame;
  }
  return out;
}

function hasCommittedEdit(
  frameEdits: Partial<Record<KeypointName, EditedKeypoint>> | undefined
): boolean {
  if (!frameEdits) return false;
  for (const name of Object.keys(frameEdits) as KeypointName[]) {
    const edit = frameEdits[name];
    if (edit && !edit.seeded) return true;
  }
  return false;
}

/** The set of frame indices that carry at least one committed edit on any side.
 *  secPose v2 carry-forward seeds are ignored until saved or manually touched. */
export function editedFrameSet(
  s: Pick<EditsSlice, 'editsFrontLeft' | 'editsFrontRight' | 'editsLeft' | 'editsRight'>
): Set<number> {
  const out = new Set<number>();
  for (const map of [s.editsFrontLeft, s.editsFrontRight, s.editsLeft, s.editsRight]) {
    for (const k of Object.keys(map)) {
      const frame = Number(k);
      if (hasCommittedEdit(map[frame])) out.add(frame);
    }
  }
  return out;
}

export function frameHasNoEdits(
  s: Pick<EditsSlice, 'editsFrontLeft' | 'editsFrontRight' | 'editsLeft' | 'editsRight'>,
  frame: number
): boolean {
  return (
    !hasCommittedEdit(s.editsFrontLeft[frame]) &&
    !hasCommittedEdit(s.editsFrontRight[frame]) &&
    !hasCommittedEdit(s.editsLeft[frame]) &&
    !hasCommittedEdit(s.editsRight[frame])
  );
}

export function commitSeededFrame(
  s: Pick<EditsSlice, 'editsFrontLeft' | 'editsFrontRight' | 'editsLeft' | 'editsRight'>,
  frame: number
): void {
  for (const map of [s.editsFrontLeft, s.editsFrontRight, s.editsLeft, s.editsRight]) {
    const frameEdits = map[frame];
    if (!frameEdits) continue;
    for (const name of Object.keys(frameEdits) as KeypointName[]) {
      const edit = frameEdits[name];
      if (edit?.seeded) delete edit.seeded;
    }
  }
}

export function unauditClearedFrames(
  s: Pick<
    AppState,
    'editsFrontLeft' | 'editsFrontRight' | 'editsLeft' | 'editsRight' | 'auditedFrames'
  >,
  frames: Iterable<number>
): void {
  for (const frame of frames) {
    if (!frameHasNoEdits(s, frame)) continue;
    const index = s.auditedFrames.indexOf(frame);
    if (index >= 0) s.auditedFrames.splice(index, 1);
  }
}

function cloneEdit(e: EditedKeypoint): EditedKeypoint {
  return {
    xy: [e.xy[0], e.xy[1]],
    visibility: e.visibility,
    locked: e.locked,
  };
}

function editsEqual(a: EditedKeypoint, b: EditedKeypoint): boolean {
  return (
    a.xy[0] === b.xy[0] &&
    a.xy[1] === b.xy[1] &&
    a.visibility === b.visibility &&
    a.locked === b.locked
  );
}

function pickPreds(state: AppState, side: Side) {
  if (side === 'front_left') return state.predFrontLeft;
  if (side === 'front_right') return state.predFrontRight;
  if (side === 'left') return state.predLeft;
  return state.predRight;
}

function pickEditsRead(state: AppState, side: Side): EditMap {
  if (side === 'front_left') return state.editsFrontLeft;
  if (side === 'front_right') return state.editsFrontRight;
  if (side === 'left') return state.editsLeft;
  return state.editsRight;
}

// When `navigableFrames` is set (picks loaded), edits are only allowed on
// listed frames. Null means full access (no picks loaded).
function frameAllowed(state: AppState, frame: number): boolean {
  const nav = state.navigableFrames;
  if (!nav || nav.length === 0) return true;
  return nav.includes(frame);
}

export const createEditsSlice: StateCreator<
  AppState,
  [['zustand/immer', never]],
  [],
  EditsSlice
> = (set, get) => ({
  editsFrontLeft: {},
  editsFrontRight: {},
  editsLeft: {},
  editsRight: {},
  setKeypoint: (side, frame, name, edit) => {
    const state = get();
    if (!frameAllowed(state, frame)) return;
    const preds = pickPreds(state, side);
    const record = preds?.[frame]?.find((r) => r.name === name);
    if (!record) return;
    // Qualified keypoints are read-only by default. Once the user explicitly
    // unlocks one via toggleQualifiedKeypoint (Space key), an edit exists and
    // subsequent drag/visibility writes are allowed.
    const hasExistingEdit = !!pickEditsRead(state, side)[frame]?.[name];
    if (record.qualified && !hasExistingEdit) return;

    const normalized: EditedKeypoint =
      edit.visibility === 'out_of_frame' ? { ...edit, xy: [-1, -1] } : edit;
    const next = cloneEdit(normalized);

    const editsMap = pickEditsRead(state, side);
    const existing = editsMap[frame]?.[name];
    if (existing && editsEqual(existing, next)) return;
    const prev = existing ? cloneEdit(existing) : null;

    set((s) => {
      commitSeededFrame(s, frame);
      const map =
        side === 'front_left'
          ? s.editsFrontLeft
          : side === 'front_right'
            ? s.editsFrontRight
            : side === 'left'
              ? s.editsLeft
              : s.editsRight;
      const frameMap = map[frame] ?? {};
      frameMap[name] = next;
      map[frame] = frameMap;
    });

    state.pushHistory([{ side, frame, name, prev, next }]);
  },
  setVisibility: (side, frame, name, visibility) => {
    const state = get();
    if (!frameAllowed(state, frame)) return;

    // Compute the next edit for a target side, preserving that side's own
    // position (and locked flag). Restoring out of out_of_frame re-seeds xy
    // from the prediction; out_of_frame normalizes xy to [-1, -1].
    const buildNext = (targetSide: Side): EditedKeypoint | null => {
      const preds = pickPreds(state, targetSide);
      const record = preds?.[frame]?.find((r) => r.name === name);
      if (!record) return null;
      const existing = pickEditsRead(state, targetSide)[frame]?.[name];
      const locked = existing?.locked ?? false;
      let xy: [number, number];
      if (visibility === 'out_of_frame') {
        xy = [-1, -1];
      } else if (existing && existing.visibility !== 'out_of_frame') {
        xy = [existing.xy[0], existing.xy[1]];
      } else if (state.annotationMode === 'v2' && record.defaultVisibility === 'out_of_frame') {
        const size = state.imageSizes[targetSide] ?? state.imageSize;
        xy = defaultPose(size)[name];
      } else {
        xy = [record.xy[0], record.xy[1]];
      }
      return { xy, visibility, locked };
    };

    const batch: HistoryEntry = [];
    const writes: { side: Side; next: EditedKeypoint }[] = [];

    const stage = (targetSide: Side, forced: boolean): void => {
      const preds = pickPreds(state, targetSide);
      const record = preds?.[frame]?.find((r) => r.name === name);
      if (!record) return;
      const existing = pickEditsRead(state, targetSide)[frame]?.[name];
      // Source side honors the qualified read-only guard; the propagated
      // front_right write is forced (always overwrites).
      if (!forced && record.qualified && !existing) return;
      const next = buildNext(targetSide);
      if (!next) return;
      if (existing && editsEqual(existing, next)) return;
      batch.push({
        side: targetSide,
        frame,
        name,
        prev: existing ? cloneEdit(existing) : null,
        next,
      });
      writes.push({ side: targetSide, next });
    };

    stage(side, false);
    if (side === 'front_left') stage('front_right', true);

    if (writes.length === 0) return;

    set((s) => {
      commitSeededFrame(s, frame);
      for (const { side: wSide, next } of writes) {
        const map =
          wSide === 'front_left'
            ? s.editsFrontLeft
            : wSide === 'front_right'
              ? s.editsFrontRight
              : wSide === 'left'
                ? s.editsLeft
                : s.editsRight;
        const frameMap = map[frame] ?? {};
        frameMap[name] = next;
        map[frame] = frameMap;
      }
    });

    state.pushHistory(batch);
  },
  toggleQualifiedKeypoint: (side, frame, name) => {
    const state = get();
    if (!frameAllowed(state, frame)) return;
    const preds = pickPreds(state, side);
    const record = preds?.[frame]?.find((r) => r.name === name);
    if (!record || !record.qualified) return;

    const editsMap = pickEditsRead(state, side);
    const existing = editsMap[frame]?.[name];

    if (!existing) {
      // First unlock: materialise a draggable edit from the predicted position.
      const next: EditedKeypoint = {
        xy: [record.xy[0], record.xy[1]],
        visibility: 'visible',
        locked: false,
      };
      set((s) => {
        const map =
          side === 'front_left'
            ? s.editsFrontLeft
            : side === 'front_right'
              ? s.editsFrontRight
              : side === 'left'
                ? s.editsLeft
                : s.editsRight;
        const frameMap = map[frame] ?? {};
        frameMap[name] = next;
        map[frame] = frameMap;
      });
      state.pushHistory([{ side, frame, name, prev: null, next }]);
    } else {
      // Subsequent presses: toggle locked.
      const next: EditedKeypoint = { ...existing, locked: !existing.locked };
      set((s) => {
        const map =
          side === 'front_left'
            ? s.editsFrontLeft
            : side === 'front_right'
              ? s.editsFrontRight
              : side === 'left'
                ? s.editsLeft
                : s.editsRight;
        const frameMap = map[frame] ?? {};
        frameMap[name] = next;
        map[frame] = frameMap;
      });
      state.pushHistory([{ side, frame, name, prev: cloneEdit(existing), next }]);
    }
  },
  clearKeypoint: (side, frame, name) => {
    const state = get();
    if (!frameAllowed(state, frame)) return;
    const editsMap = pickEditsRead(state, side);
    const existing = editsMap[frame]?.[name];
    if (!existing) return;
    const prev = cloneEdit(existing);

    set((s) => {
      commitSeededFrame(s, frame);
      const map =
        side === 'front_left'
          ? s.editsFrontLeft
          : side === 'front_right'
            ? s.editsFrontRight
            : side === 'left'
              ? s.editsLeft
              : s.editsRight;
      const frameMap = map[frame];
      if (!frameMap) return;
      delete frameMap[name];
      if (Object.keys(frameMap).length === 0) {
        delete map[frame];
      }
      unauditClearedFrames(s, [frame]);
    });

    state.pushHistory([{ side, frame, name, prev, next: null }]);
  },
  setAllLocked: (side, frame, locked) => {
    const state = get();
    if (!frameAllowed(state, frame)) return;
    const preds = pickPreds(state, side);
    if (!preds?.[frame]) return;
    const editsMap = pickEditsRead(state, side);

    const batch: HistoryEntry = [];
    const writes: { name: KeypointName; next: EditedKeypoint }[] = [];
    for (const { name } of KEYPOINTS) {
      const record = preds[frame].find((r) => r.name === name);
      if (!record || record.qualified) continue;
      const existing = editsMap[frame]?.[name];
      const effective: EditedKeypoint = existing
        ? cloneEdit(existing)
        : { xy: [record.xy[0], record.xy[1]], visibility: 'visible', locked: false };
      if (effective.locked === locked) continue;
      const next: EditedKeypoint = { ...effective, locked };
      const prev = existing ? cloneEdit(existing) : null;
      const change: EditChange = { side, frame, name, prev, next };
      batch.push(change);
      writes.push({ name, next });
    }
    if (batch.length === 0) return;

    set((s) => {
      const map =
        side === 'front_left'
          ? s.editsFrontLeft
          : side === 'front_right'
            ? s.editsFrontRight
            : side === 'left'
              ? s.editsLeft
              : s.editsRight;
      const frameMap = map[frame] ?? {};
      for (const { name, next } of writes) {
        frameMap[name] = next;
      }
      map[frame] = frameMap;
    });

    state.pushHistory(batch);
  },
  resetFrame: (side, frame) => {
    const state = get();
    if (!frameAllowed(state, frame)) return;
    const editsMap = pickEditsRead(state, side);
    const frameEdits = editsMap[frame];
    if (!frameEdits) return;

    const batch: HistoryEntry = [];
    for (const [name, edit] of Object.entries(frameEdits)) {
      if (!edit) continue;
      batch.push({
        side,
        frame,
        name: name as KeypointName,
        prev: cloneEdit(edit),
        next: null,
      });
    }
    if (batch.length === 0) return;

    set((s) => {
      const map =
        side === 'front_left'
          ? s.editsFrontLeft
          : side === 'front_right'
            ? s.editsFrontRight
            : side === 'left'
              ? s.editsLeft
              : s.editsRight;
      delete map[frame];
      unauditClearedFrames(s, [frame]);
    });

    state.pushHistory(batch);
  },
  resetAllEdits: () =>
    set((s) => {
      s.editsFrontLeft = {};
      s.editsFrontRight = {};
      s.editsLeft = {};
      s.editsRight = {};
      s.auditedFrames = [];
      s.past.length = 0;
      s.future.length = 0;
    }),
  restoreFromDraft: (draft) =>
    set((s) => {
      s.editsFrontLeft = rebuildEditMap(draft.front_left.edits);
      s.editsFrontRight = rebuildEditMap(draft.front_right.edits);
      s.editsLeft = draft.left ? rebuildEditMap(draft.left.edits) : {};
      s.editsRight = draft.right ? rebuildEditMap(draft.right.edits) : {};
      // Audit progress lives in the video slice but rides along in the draft.
      s.auditedFrames = draft.audited ? [...draft.audited].sort((a, b) => a - b) : [];
      // Bank prior sessions' time; the clock is (re)started on annotate entry.
      s.timerBaseMs = draft.elapsedMs ?? 0;
      s.timerStartedAt = null;
      s.past.length = 0;
      s.future.length = 0;
    }),
  setInitialEdits: (initial) =>
    set((s) => {
      s.editsFrontLeft = rebuildEditMap(initial.front_left);
      s.editsFrontRight = rebuildEditMap(initial.front_right);
      s.editsLeft = rebuildEditMap(initial.left);
      s.editsRight = rebuildEditMap(initial.right);
      // Fresh load starts with nothing audited; a resumed draft overrides this.
      s.auditedFrames = [];
      // Fresh load starts the clock at zero; a resumed draft overrides this.
      s.timerBaseMs = 0;
      s.timerStartedAt = null;
      s.past.length = 0;
      s.future.length = 0;
    }),
  seedFrameEdits: (frame, seeds) => {
    const state = get();
    if (!frameAllowed(state, frame)) return;
    set((s) => {
      for (const side of Object.keys(seeds) as Side[]) {
        const sideSeed = seeds[side];
        const map =
          side === 'front_left'
            ? s.editsFrontLeft
            : side === 'front_right'
              ? s.editsFrontRight
              : side === 'left'
                ? s.editsLeft
                : s.editsRight;
        if (!sideSeed || Object.keys(sideSeed).length === 0) {
          delete map[frame];
          continue;
        }
        const frameMap: Partial<Record<KeypointName, EditedKeypoint>> = {};
        for (const name of Object.keys(sideSeed) as KeypointName[]) {
          const edit = sideSeed[name];
          if (!edit) continue;
          frameMap[name] = {
            xy: [edit.xy[0], edit.xy[1]],
            visibility: edit.visibility,
            locked: false,
            seeded: true,
          };
        }
        map[frame] = frameMap;
      }
    });
  },
  commitFrameSeeds: (frame) =>
    set((s) => {
      commitSeededFrame(s, frame);
    }),
});
